/**
 * Defines the frontend's complete HTTP boundary with the Vault subsystem.
 *
 * Components express product operations through typed functions rather than
 * constructing route paths or provider payloads. Centralizing the contract
 * keeps catalog, item, sharing, and job semantics aligned
 * with backend authorization boundaries.
 */
import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import type {
  FolderContentsResponse,
  ItemListResponse,
  ItemResponse,
  SharingResponse,
  UploadSession,
  VaultCatalogEntry,
  VaultRole,
  VaultShareCandidates,
  VaultVersion,
  WorkspaceVaultPairing,
  WorkspaceVaultStatus,
} from '../types'

const catalogPath = (orgId: string) => `/org/${encodeURIComponent(orgId)}/vaults`
const path = (orgId: string, vaultId: string) =>
  `${catalogPath(orgId)}/${encodeURIComponent(vaultId)}`

export const listVaults = async (
  api: NodeApiClient,
  orgId: string,
) => (await api.get<{ vaults: VaultCatalogEntry[] }>(catalogPath(orgId))).data.vaults

const pairingPath = (orgId: string, workspaceId: string) =>
  `${catalogPath(orgId)}/workspaces/${encodeURIComponent(workspaceId)}/pairing`

export const getWorkspaceVaultPairing = async (
  api: NodeApiClient,
  orgId: string,
  workspaceId: string,
) => (await api.get<WorkspaceVaultPairing | null>(pairingPath(orgId, workspaceId))).data

export const getWorkspaceVaultStatus = async (
  api: NodeApiClient,
  orgId: string,
  workspaceId: string,
) => (await api.get<WorkspaceVaultStatus>(
  `${pairingPath(orgId, workspaceId)}/status`,
)).data

export const enableWorkspaceVault = async (
  api: NodeApiClient,
  orgId: string,
  workspaceId: string,
  name: string,
) => (await api.post<WorkspaceVaultPairing>(pairingPath(orgId, workspaceId), {
  name,
  description: 'Workspace document intake',
})).data

export const disableWorkspaceVault = async (
  api: NodeApiClient,
  orgId: string,
  workspaceId: string,
) => (await api.delete<WorkspaceVaultPairing>(pairingPath(orgId, workspaceId))).data

export const listChildren = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  folderId: string,
  nextToken?: string,
) => (await api.get<FolderContentsResponse>(
  `${path(orgId, vaultId)}/folders/${encodeURIComponent(folderId)}/children${nextToken ? `?next_token=${encodeURIComponent(nextToken)}` : ''}`,
)).data

export const createFolder = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  parentId: string,
  name: string,
) => (await api.post<ItemResponse>(`${path(orgId, vaultId)}/folders`, { parentId, name })).data

export const createNote = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  name: string,
  body = '',
) => (await api.post<ItemResponse>(`${path(orgId, vaultId)}/notes`, { name, body })).data

export const saveNote = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  itemId: string,
  body: string,
  expectedRevision: number,
) => (await api.patch<ItemResponse>(`${path(orgId, vaultId)}/notes/${encodeURIComponent(itemId)}`, {
  body,
  expectedRevision,
})).data

export const trashItem = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  itemId: string,
) => api.delete(`${path(orgId, vaultId)}/items/${encodeURIComponent(itemId)}`)

export const restoreItem = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  itemId: string,
) => (await api.post<ItemResponse>(
  `${path(orgId, vaultId)}/items/${encodeURIComponent(itemId)}/restore`,
)).data

export const updateItem = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  itemId: string,
  expectedRevision: number,
  changes: { name?: string; parentId?: string; confirmPermissionChange?: boolean },
) => (await api.patch<ItemResponse>(`${path(orgId, vaultId)}/items/${encodeURIComponent(itemId)}`, {
  expectedRevision,
  ...changes,
})).data

export const copyItem = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  itemId: string,
  parentId: string,
  name?: string,
) => (await api.post<{ item?: ItemResponse; jobId?: string; status: string }>(
  `${path(orgId, vaultId)}/items/${encodeURIComponent(itemId)}/copy`,
  { parentId, name },
)).data

export const downloadItem = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  itemId: string,
) => (await api.get<{ url: string }>(
  `${path(orgId, vaultId)}/files/${encodeURIComponent(itemId)}/download-url`,
)).data.url

export const startVaultDownload = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  itemIds: string[],
) => (await api.post<{ jobId: string; status: string }>(
  `${path(orgId, vaultId)}/downloads`,
  { itemIds },
)).data

export const getVaultDownload = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  jobId: string,
) => (await api.get<{ url: string }>(
  `${path(orgId, vaultId)}/downloads/${encodeURIComponent(jobId)}`,
)).data.url

export const listFileVersions = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  itemId: string,
) => (await api.get<VaultVersion[]>(
  `${path(orgId, vaultId)}/files/${encodeURIComponent(itemId)}/versions`,
)).data

export const downloadFileVersion = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  itemId: string,
  versionId: string,
) => (await api.get<{ url: string }>(
  `${path(orgId, vaultId)}/files/${encodeURIComponent(itemId)}/versions/${encodeURIComponent(versionId)}/download-url`,
)).data.url

export const uploadFile = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  parentId: string,
  file: File,
) => {
  const session = (await api.post<UploadSession>(`${path(orgId, vaultId)}/files/uploads`, {
    parentId,
    filename: file.name,
    sizeBytes: file.size,
    mimeType: file.type || 'application/octet-stream',
  })).data
  await putUpload(session, file)
  return (await api.post<ItemResponse>(
    `${path(orgId, vaultId)}/files/uploads/${session.uploadId}/complete`,
  )).data
}

export const uploadFileVersion = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  itemId: string,
  file: File,
) => {
  const session = (await api.post<UploadSession>(
    `${path(orgId, vaultId)}/files/${encodeURIComponent(itemId)}/versions/uploads`,
    { sizeBytes: file.size, mimeType: file.type || 'application/octet-stream' },
  )).data
  await putUpload(session, file)
  return (await api.post<ItemResponse>(
    `${path(orgId, vaultId)}/files/uploads/${session.uploadId}/complete`,
  )).data
}

const putUpload = async (session: UploadSession, file: File) => {
  const metadataHeaders = Object.fromEntries(
    Object.entries(session.metadata).map(([key, value]) => [`x-amz-meta-${key}`, value]),
  )
  const response = await fetch(session.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'X-Amz-Tagging': session.tagging,
      ...metadataHeaders,
    },
  })
  if (!response.ok) throw new Error(`Upload failed (${response.status})`)
}

export const searchItems = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  query: string,
) => (await api.get<ItemListResponse>(
  `${path(orgId, vaultId)}/search?q=${encodeURIComponent(query)}`,
)).data

export const listSpecial = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  view: 'notes' | 'shared-with-me' | 'trash',
) => (await api.get<ItemListResponse>(`${path(orgId, vaultId)}/${view}`)).data

export const getSharing = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  itemId: string,
) => (await api.get<SharingResponse>(
  `${path(orgId, vaultId)}/items/${encodeURIComponent(itemId)}/sharing`,
)).data

export const setCollaborator = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  itemId: string,
  userId: string,
  role: Extract<VaultRole, 'viewer' | 'editor'>,
) => (await api.put<SharingResponse>(
  `${path(orgId, vaultId)}/items/${encodeURIComponent(itemId)}/collaborators/${encodeURIComponent(userId)}`,
  { role },
)).data

export const removeCollaborator = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  itemId: string,
  userId: string,
) => api.delete(
  `${path(orgId, vaultId)}/items/${encodeURIComponent(itemId)}/collaborators/${encodeURIComponent(userId)}`,
)

export const setOrganizationCollaborator = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  itemId: string,
  role: Extract<VaultRole, 'viewer' | 'editor'>,
) => (await api.put<SharingResponse>(
  `${path(orgId, vaultId)}/items/${encodeURIComponent(itemId)}/organization-collaborator`,
  { role },
)).data

export const removeOrganizationCollaborator = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  itemId: string,
) => api.delete(
  `${path(orgId, vaultId)}/items/${encodeURIComponent(itemId)}/organization-collaborator`,
)

export const listShareCandidates = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
) => (await api.get<VaultShareCandidates>(
  `${path(orgId, vaultId)}/share-candidates`,
)).data
